<div [class]="config().customClass || ''">
  <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col gap-4 sm:gap-6">
    <div [class]="getLayoutClass()">
      @for (field of sortedFields(); track field.key) {
        <div [class]="field.span && config().layout === 'grid' ? getGridColClass(field.span) : ''">
          <!-- Text Inputs -->
          @if (field.type === 'text' || field.type === 'email' || field.type === 'tel' || field.type === 'url') {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label [for]="field.key" class="block text-sm font-medium text-black">
                  {{ field.label }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
              }
              @if (field.showClear) {
                <p-iconfield>
                  <input
                    [id]="field.key"
                    [type]="field.type"
                    pInputText
                    [formControlName]="field.key"
                    [placeholder]="field.placeholder || ''"
                    [readonly]="field.readonly"
                    class="w-full text-black placeholder:text-black"
                    [class.border-red-500]="isFieldInvalid(field.key)"
                  />
                  @if (hasValue(field.key)) {
                    <p-inputicon
                      styleClass="pi pi-times cursor-pointer"
                      [style.color]="iconColor()"
                      (click)="clearField(field.key)"
                      (keydown.enter)="clearField(field.key)"
                      (keydown.space)="clearField(field.key)"
                      [attr.role]="'button'"
                      [attr.tabindex]="0"
                      [attr.aria-label]="'Clear ' + (field.label || field.placeholder || field.key)"
                    />
                  }
                </p-iconfield>
              } @else {
                <input
                  [id]="field.key"
                  [type]="field.type"
                  pInputText
                  [formControlName]="field.key"
                  [placeholder]="field.placeholder || ''"
                  [readonly]="field.readonly"
                  class="w-full text-black placeholder:text-black"
                  [class.border-red-500]="isFieldInvalid(field.key)"
                />
              }
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          <!-- Password -->
          @if (field.type === 'password') {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label [for]="field.key" class="block text-sm font-medium text-black">
                  {{ field.label }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
              }
              @if (field.showClear) {
                <div class="relative">
                  <p-password
                    [id]="field.key"
                    [formControlName]="field.key"
                    [placeholder]="field.placeholder || ''"
                    [feedback]="false"
                    [toggleMask]="true"
                    class="w-full"
                    inputStyleClass="w-full pr-20"
                    [inputStyle]="{ width: '100%', paddingRight: '5rem', color: '#000000' }"
                    [class.border-red-500]="isFieldInvalid(field.key)"
                  />
                  @if (hasValue(field.key)) {
                    <i
                      class="pi pi-times cursor-pointer absolute right-12 top-1/2 -translate-y-1/2"
                      [style.color]="iconColor()"
                      (click)="clearField(field.key)"
                      (keydown.enter)="clearField(field.key)"
                      (keydown.space)="clearField(field.key)"
                      [attr.role]="'button'"
                      [attr.tabindex]="0"
                      [attr.aria-label]="'Clear ' + (field.label || field.placeholder || field.key)"
                    ></i>
                  }
                </div>
              } @else {
                <p-password
                  [id]="field.key"
                  [formControlName]="field.key"
                  [placeholder]="field.placeholder || ''"
                  [feedback]="false"
                  [toggleMask]="true"
                  class="w-full"
                  inputStyleClass="w-full"
                  [inputStyle]="{ width: '100%', color: '#000000' }"
                  [class.border-red-500]="isFieldInvalid(field.key)"
                />
              }
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          <!-- Number -->
          @if (field.type === 'number') {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label [for]="field.key" class="block text-sm font-medium text-black">
                  {{ field.label }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
              }
              <input
                [id]="field.key"
                type="number"
                pInputText
                [formControlName]="field.key"
                [placeholder]="field.placeholder || ''"
                [min]="field.validation?.min ?? null"
                [max]="field.validation?.max ?? null"
                [readonly]="field.readonly"
                class="w-full text-black placeholder:text-black"
                [class.border-red-500]="isFieldInvalid(field.key)"
                [disabled]="field.disabled || disabled()"
              />
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          <!-- Textarea -->
          @if (field.type === 'textarea') {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label [for]="field.key" class="block text-sm font-medium text-black">
                  {{ field.label }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
              }
              <textarea
                [id]="field.key"
                pInputText
                [formControlName]="field.key"
                [placeholder]="field.placeholder || ''"
                [rows]="field.rows || 3"
                [readonly]="field.readonly"
                class="w-full text-black placeholder:text-black"
                [class.border-red-500]="isFieldInvalid(field.key)"
                [disabled]="field.disabled || disabled()"
              ></textarea>
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          <!-- Select -->
          @if (field.type === 'select' && field.options) {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label [for]="field.key" class="block text-sm font-medium text-black">
                  {{ field.label }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
              }
              @if (field.multiselect) {
                <p-multiselect
                  [id]="field.key"
                  [formControlName]="field.key"
                  [options]="field.options"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="field.placeholder || 'Select options'"
                  class="w-full"
                  [class.border-red-500]="isFieldInvalid(field.key)"
                  [disabled]="field.disabled || disabled()"
                  display="chip"
                  [showClear]="field.allowClear ?? true"
                  appendTo="body"
                />
              } @else {
                <p-select
                  [id]="field.key"
                  [formControlName]="field.key"
                  [options]="field.options"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="field.placeholder || 'Select an option'"
                  class="w-full"
                  [class.border-red-500]="isFieldInvalid(field.key)"
                  [disabled]="field.disabled || disabled()"
                  [showClear]="field.allowClear ?? false"
                />
              }
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          <!-- Checkbox -->
          @if (field.type === 'checkbox') {
            <div class="flex flex-col gap-2">
              <div class="flex flex-col sm:flex-row items-start sm:items-center" [class.sm:justify-between]="field.actionLink" [class.gap-2]="!field.actionLink">
                <div class="flex items-center gap-2">
                  <p-checkbox
                    [id]="field.key"
                    [formControlName]="field.key"
                    [binary]="true"
                    [disabled]="field.disabled || disabled() || disabledKeysSignal().has(field.key)"
                  />
                  <label [for]="field.key" class="text-sm font-medium cursor-pointer m-0" [style.color]="iconColor()">
                    {{ field.label }}
                    @if (field.required) {
                      <span class="text-red-500">*</span>
                    }
                  </label>
                </div>
                @if (field.actionLink) {
                  <a
                    [href]="field.actionLink.href || '#'"
                    [class]="field.actionLink.class || 'text-sm font-medium no-underline hover:underline'"
                    [style.color]="iconColor()"
                    (click)="$event.preventDefault(); actionLinkClick.emit(field.key)"
                  >
                    {{ field.actionLink.label }}
                  </a>
                }
              </div>
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          @if (field.type === 'toggle') {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label class="block text-sm font-medium text-black">{{ field.label }}</label>
              }
              <div class="flex items-center gap-3">
                <p-toggleswitch
                  [id]="field.key"
                  [formControlName]="field.key"
                  [disabled]="field.disabled || disabled()"
                />
                <label [for]="field.key" class="text-sm font-medium cursor-pointer m-0" [style.color]="iconColor()">
                  {{ form.get(field.key)?.value ? (field.options?.[0]?.label ?? '') : (field.options?.[1]?.label ?? '') }}
                </label>
              </div>
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          <!-- Radio -->
          @if (field.type === 'radio' && field.options) {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label class="block text-sm font-medium text-black">
                  {{ field.label }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
              }
              <div class="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4">
                @for (option of field.options; track option.value) {
                  <div class="flex items-center gap-2">
                    <p-radiobutton
                      [name]="field.key"
                      [inputId]="field.key + '-' + option.value"
                      [value]="option.value"
                      [formControlName]="field.key"
                      [disabled]="option.disabled || field.disabled || disabled()"
                    />
                    <label [for]="field.key + '-' + option.value" class="text-sm text-black cursor-pointer m-0">
                      {{ option.label }}
                    </label>
                  </div>
                }
              </div>
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          <!-- Date -->
          @if (field.type === 'date') {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label [for]="field.key" class="block text-sm font-medium text-black">
                  {{ field.label }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
              }
              <p-iconfield>
                <input
                  [id]="field.key"
                  type="date"
                  pInputText
                  [formControlName]="field.key"
                  [placeholder]="field.placeholder || ''"
                  class="w-full text-black placeholder:text-black"
                  [class.border-red-500]="isFieldInvalid(field.key)"
                  [disabled]="field.disabled || disabled()"
                  [min]="field.min || ''"
                  [max]="field.max || ''"
                />
                @if (hasValue(field.key)) {
                  <p-inputicon
                    styleClass="pi pi-times cursor-pointer"
                    [style.color]="iconColor()"
                    (click)="clearField(field.key)"
                    (keydown.enter)="clearField(field.key)"
                    (keydown.space)="clearField(field.key)"
                    [attr.role]="'button'"
                    [attr.tabindex]="0"
                    [attr.aria-label]="'Clear ' + (field.label || field.key)"
                  />
                }
              </p-iconfield>
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          <!-- Time -->
          @if (field.type === 'time') {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label [for]="field.key" class="block text-sm font-medium text-black">
                  {{ field.label }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
              }
              <input
                [id]="field.key"
                type="time"
                pInputText
                [formControlName]="field.key"
                [placeholder]="field.placeholder || ''"
                class="w-full text-black placeholder:text-black"
                [class.border-red-500]="isFieldInvalid(field.key)"
                [disabled]="field.disabled || disabled()"
                [min]="field.min || ''"
                [max]="field.max || ''"
              />
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }

          <!-- DateTime Local -->
          @if (field.type === 'datetime-local') {
            <div class="flex flex-col gap-2">
              @if (field.label) {
                <label [for]="field.key" class="block text-sm font-medium text-black">
                  {{ field.label }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
              }
              <input
                [id]="field.key"
                type="datetime-local"
                pInputText
                [formControlName]="field.key"
                [placeholder]="field.placeholder || ''"
                class="w-full text-black placeholder:text-black"
                pInputText
                [formControlName]="field.key"
                [placeholder]="field.placeholder || ''"
                class="w-full"
                [class.border-red-500]="isFieldInvalid(field.key)"
                [disabled]="field.disabled || disabled()"
              />
              @if (getFieldError(field.key)) {
                <p class="text-sm text-red-500 m-0">{{ getFieldError(field.key) }}</p>
              }
              @if (field.hint && !getFieldError(field.key)) {
                <p class="text-xs text-gray-500 m-0">{{ field.hint }}</p>
              }
            </div>
          }
        </div>
      }
    </div>

    <!-- Form Actions -->
    @if (config().submitLabel || config().showReset) {
      <div class="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-end">
        @if (config().showReset) {
          <p-button
            type="button"
            [label]="config().resetLabel || 'Reset'"
            [severity]="isFormDirty() ? 'primary' : 'secondary'"
            (onClick)="onReset()"
            [disabled]="disabled() || !isFormDirty()"
            class="w-full sm:w-auto"
            [styleClass]="isFormDirty() ? 'reset-button-active' : 'reset-button-inactive'"
          />
        }
        @if (config().submitLabel) {
          <p-button 
            type="submit"
            [label]="config().submitLabel"
            [icon]="config().submitIcon || ''"
            [disabled]="disabled() || form.invalid"
            [styleClass]="isFormValid() ? 'submit-button-active' : 'submit-button-inactive'"
            [style.background-color]="isFormValid() ? submitButtonColor() : undefined"
            [style.border-color]="isFormValid() ? submitButtonColor() : undefined"
          />
        }
      </div>
    }
  </form>
</div>
